// ============================================================
// Standortmonitor — Library — NgXS State
// Pool of all saved data slices. Persisted locally via
// @ngxs/storage-plugin (see root.config.ts, keys: [LIBRARY_STATE]).
// Replaces the hand-rolled window.PDC store from the prototype.
// ============================================================

import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { LIBRARY_STATE } from '../index';
import { LibraryStateModel, DataSlice } from './library.model';
import { LibraryAction } from './library.action';
import { Auth } from '../auth/auth.action';
import { sliceSignature } from './library.util';

@State<LibraryStateModel>({
  name: LIBRARY_STATE,
  defaults: {
    slices: [],
  },
})
@Injectable()
export class LibraryState {

  @Selector()
  static slices(state: LibraryStateModel): DataSlice[] {
    return state.slices;
  }

  @Selector()
  static favorites(state: LibraryStateModel): DataSlice[] {
    return state.slices.filter((slice) => slice.fav);
  }

  @Selector()
  static count(state: LibraryStateModel): number {
    return state.slices.length;
  }

  /** All existing signatures — for the "already saved?" check. */
  @Selector()
  static signatures(state: LibraryStateModel): string[] {
    return state.slices.map((slice) => slice.signature);
  }

  /** Ensure a complete slice with signature set. */
  private _withSignature(slice: DataSlice): DataSlice {
    return slice.signature ? slice : { ...slice, signature: sliceSignature(slice) };
  }

  @Action(LibraryAction.AddSlice)
  addSlice(ctx: StateContext<LibraryStateModel>, { slice }: LibraryAction.AddSlice) {
    const normalized = this._withSignature(slice);
    const state = ctx.getState();
    if (state.slices.some((s) => s.signature === normalized.signature)) {
      return;
    }
    ctx.patchState({ slices: [normalized, ...state.slices] });
  }

  @Action(LibraryAction.RemoveSlice)
  removeSlice(ctx: StateContext<LibraryStateModel>, { sliceId }: LibraryAction.RemoveSlice) {
    ctx.patchState({
      slices: ctx.getState().slices.filter((slice) => slice.id !== sliceId),
    });
  }

  @Action(LibraryAction.ToggleSlice)
  toggleSlice(ctx: StateContext<LibraryStateModel>, { slice }: LibraryAction.ToggleSlice) {
    const normalized = this._withSignature(slice);
    const state = ctx.getState();
    const existing = state.slices.find((s) => s.signature === normalized.signature);
    if (existing) {
      ctx.patchState({
        slices: state.slices.filter((s) => s.signature !== normalized.signature),
      });
      return;
    }
    ctx.patchState({ slices: [normalized, ...state.slices] });
  }

  @Action(LibraryAction.ToggleFavorite)
  toggleFavorite(ctx: StateContext<LibraryStateModel>, { sliceId }: LibraryAction.ToggleFavorite) {
    ctx.patchState({
      slices: ctx.getState().slices.map((slice) =>
        slice.id === sliceId ? { ...slice, fav: !slice.fav } : slice
      ),
    });
  }

  @Action(LibraryAction.SetSlices)
  setSlices(ctx: StateContext<LibraryStateModel>, { slices }: LibraryAction.SetSlices) {
    ctx.patchState({ slices });
  }

  @Action(LibraryAction.UpsertSlice)
  upsertSlice(ctx: StateContext<LibraryStateModel>, { slice }: LibraryAction.UpsertSlice) {
    const normalized = this._withSignature(slice);
    const state = ctx.getState();
    const without = state.slices.filter((s) => s.signature !== normalized.signature);
    ctx.patchState({ slices: [normalized, ...without] });
  }

  @Action(LibraryAction.ClearLibrary)
  clearLibrary(ctx: StateContext<LibraryStateModel>) {
    ctx.patchState({ slices: [] });
  }

  /** Slices belong to the logged-in user; never carry them over to the next login. */
  @Action(Auth.Logout)
  clearOnLogout(ctx: StateContext<LibraryStateModel>) {
    ctx.patchState({ slices: [] });
  }

  @Action(LibraryAction.UpdateSlicePreview)
  updateSlicePreview(
    ctx: StateContext<LibraryStateModel>,
    { sliceId, previewSeries, datasetBuiltAt }: LibraryAction.UpdateSlicePreview,
  ) {
    ctx.patchState({
      slices: ctx.getState().slices.map((slice) =>
        slice.id === sliceId
          ? { ...slice, previewSeries, datasetBuiltAt }
          : slice,
      ),
    });
  }
}
